import type { AuthConfigNanoId, ConnectedAccountNanoId } from '@composio/types'
import React, { useEffect, useMemo } from 'react'
import { useRouter } from 'next/router'
import { atom, useAtom } from 'jotai'

import { ANALYTICS_EVENTS } from '~/constants/analytics'
import { upperCaseFirstLetter } from '~/utils/string'
import { ANALYTICS } from '~/utils/tracking'
import { useToolkitInfo } from '../appScreen'
import { WizardBox } from '../components/WizardBox'
import { ConnectedAccountSetup } from './addConnectedAccount'
import { CreateIntegrationPopover } from './createIntegration/CreateIntegrationPopover'
import { RunActionPopup } from './executeAction'

const noop = () => {}

export interface E2EFlowWizardProps {
  first: {
    appName: string
  }
  second: {
    authConfigId: AuthConfigNanoId | null
  }
  third: {
    connectedAccountId: ConnectedAccountNanoId | null
    actionName: string | null
  }
  currentStep: number
}

const DEFAULT_VALUE: E2EFlowWizardProps = {
  first: {
    appName: '',
  },
  second: {
    authConfigId: null,
  },
  third: {
    connectedAccountId: null,
    actionName: null,
  },
  currentStep: 1,
}

export const wizardFlowAtoms = atom<E2EFlowWizardProps>(DEFAULT_VALUE)

export const updateWizardFlowAtom = atom(
  null,
  (
    get,
    set,
    arg: {
      step?: number
      data?: Partial<E2EFlowWizardProps>
    },
  ) => {
    const { step, data } = arg

    set(wizardFlowAtoms, (prev) => ({
      ...prev,
      ...(step !== undefined && {
        currentStep: step,
      }),
      ...data,
    }))
  },
)

export const FlowWizard = ({
  initialData,
  setOpen,
  isLocal = false,
}: {
  initialData: E2EFlowWizardProps
  setOpen: (e: boolean) => void
  isLocal?: boolean
}) => {
  const [wizardFlow, setWizardFlow] = useAtom(wizardFlowAtoms)
  const { currentStep } = wizardFlow
  const { toolkitInfo } = useToolkitInfo(wizardFlow.first.appName)

  const router = useRouter()

  const isStartFromAppScreenAndHasIntegration = useMemo(() => {
    return initialData.currentStep === 2 && !!router.query.appId
  }, [initialData.currentStep, router.query.appId])

  useEffect(() => {
    setWizardFlow(initialData)
    return () => {
      setWizardFlow(DEFAULT_VALUE)
    }
  }, [])

  useEffect(() => {
    if (!wizardFlow.first.appName) return

    ANALYTICS.track(ANALYTICS_EVENTS.E2E_FLOW_WIZARD_VIEWED, {
      appName: wizardFlow.first.appName,
      step: currentStep,
      initialStep: initialData.currentStep,
      extraData: {
        ...wizardFlow,
      },
      startedFrom: initialData.currentStep,
    })

    return () => {
      ANALYTICS.track(ANALYTICS_EVENTS.E2E_FLOW_WIZARD_DISMISSED, {
        appName: wizardFlow.first.appName,
        step: currentStep,
        initialStep: initialData.currentStep,
        extraData: {
          ...wizardFlow,
        },
      })
    }
  }, [wizardFlow])

  const title = useMemo(() => {
    if (currentStep === 1) {
      return `Connect ${upperCaseFirstLetter(wizardFlow.first.appName)} App`
    }

    if (currentStep === 2) {
      return 'Connect an account'
    }

    return 'Execute tools'
  }, [currentStep, wizardFlow.first.appName])

  if (!wizardFlow?.first?.appName) return null

  return (
    <WizardBox
      open={true}
      setOpen={(e) => {
        setOpen(e)
      }}
      currentStep={currentStep}
      title={title}
      logo={toolkitInfo?.meta.logo ?? ''}
      appName={wizardFlow.first.appName}
      steps={[
        {
          title: 'Configure Integration',
        },
        {
          title: 'Connect an account',
        },
        {
          title: 'Execute tools',
        },
      ]}
    >
      {currentStep === 1 && (
        <CreateIntegrationPopover appKey={wizardFlow.first.appName} />
      )}
      {currentStep === 2 && (
        <ConnectedAccountSetup
          authConfigId={wizardFlow.second.authConfigId!}
          appName={wizardFlow.first.appName}
          appLogo={''}
          isStartFromAppScreenAndHasIntegration={
            isStartFromAppScreenAndHasIntegration
          }
          setOpen={setOpen}
          // Provide a function for setOpen
        />
      )}
      {currentStep === 3 && (
        <RunActionPopup
          actionName={wizardFlow.third.actionName!}
          connectedAccountId={wizardFlow.third.connectedAccountId!}
          appName={wizardFlow.first.appName}
          isLocal={isLocal}
        />
      )}
    </WizardBox>
  )
}

export const E2EFlowWizard = React.memo(FlowWizard)
